import {
  Alert, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import api from '../../../api';
import type { MatchDayDetail as Detail } from '../../../api/types';

interface Props {
  id: string;
}

export default function MatchDayDetail({ id }: Props) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.matchDay(id)
      .then((response) => {
        if (!live) {
          return;
        }
        if (response.statusCode === 200 && response.matchDay) {
          setDetail(response.matchDay);
        } else {
          setError(response.messages.join(' ') || 'Nie udało się pobrać dnia meczowego');
        }
      })
      .catch((e: Error) => { if (live) { setError(e.message); } });
    return () => { live = false; };
  }, [id]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!detail) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Wczytywanie…</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      {detail.comment && (
        <Alert severity="info"><strong>Komentarz synoptyka:</strong> {detail.comment}</Alert>
      )}

      <div>
        <Typography variant="subtitle2" gutterBottom>Mecze</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Gracz 1</TableCell>
                <TableCell align="center">Wynik</TableCell>
                <TableCell align="right">Gracz 2</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.matches.map((match, index) => (
                <TableRow key={`${match.player1}-${match.player2}-${index}`} hover>
                  <TableCell>{match.player1Name}</TableCell>
                  <TableCell align="center">
                    {match.player1Score} : {match.player2Score}
                  </TableCell>
                  <TableCell align="right">{match.player2Name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div>
        <Typography variant="subtitle2" gutterBottom>Punkty</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Gracz</TableCell>
                <TableCell align="right">Punkty</TableCell>
                <TableCell align="right">Bez kar</TableCell>
                <TableCell align="center">Grał?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...detail.participants].sort((a, b) => b.points - a.points).map((p) => (
                <TableRow key={p.playerId} hover>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="right">{p.points}</TableCell>
                  <TableCell align="right">{p.pointsWithoutPenalties}</TableCell>
                  <TableCell align="center">{p.tookPart ? 'tak' : 'nie'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <div>
        <Typography variant="subtitle2" gutterBottom>Kary</Typography>
        {detail.penalties.length === 0
          ? <Typography variant="body2" color="text.secondary">Brak kar.</Typography>
          : (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {detail.penalties.map((penalty, index) => (
                <Chip
                  key={`${penalty.playerId}-${penalty.penalty}-${index}`}
                  size="small"
                  color="warning"
                  label={`${penalty.name}: ${penalty.penalty} ${penalty.value}`}
                />
              ))}
            </Stack>
          )}
      </div>
    </Stack>
  );
}
